import { Weekday } from '@app/core/models/day';

/**
 * Assigns a {@link LimitType} to each quarter of an hour in a week
 */
export class Timetable {
  /**
   * String that can be used to initialize the timetable with default values
   */
  public static readonly defaultLimitString = '000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000';

  /**
   * No times are blocked in the timetable
   */
  public static readonly blockOff = Timetable.defaultLimitString;

  private readonly encodedLimitMap: {[key: string]: number[]} = {
    0: [0, 0, 0, 0],
    2: [0, 0, 0, 1],
    3: [0, 0, 1, 0],
    4: [0, 0, 1, 1],
    5: [0, 1, 0, 0],
    6: [0, 1, 0, 1],
    7: [0, 1, 1, 0],
    8: [0, 1, 1, 1],
    9: [1, 0, 0, 0],
    A: [1, 0, 0, 1],
    B: [1, 0, 1, 0],
    C: [1, 0, 1, 1],
    D: [1, 1, 0, 0],
    E: [1, 1, 0, 1],
    F: [1, 1, 1, 0],
    1: [1, 1, 1, 1]
  };

  /**
   * - First dimension: Weekday enum (0-6)
   * - Second dimension: Hour (0-23)
   * - Third dimension: Hour quarters (0-3)
   */
  public days: LimitType[][][] = new Array(7).fill(0).map(() => new Array(24).fill(0).map(() => new Array(4).fill(LimitType.Allowed)));

  /**
   * Returns limit at given time
   * @param day Weekday
   * @param hour 0-23
   * @param quarter 0-3 (quarter of an hour)
   */
  public getLimit(day: Weekday, hour: number, quarter: number): LimitType {
    return this.days[day][hour][quarter];
  }

  /**
   * Returns limits in given hour at given day (each array index represents one quarter of the hour)
   * @param day Weekday
   * @param hour 0-23
   */
  public getHourLimits(day: Weekday, hour: number): [LimitType, LimitType, LimitType, LimitType] {
    return this.days[day][hour] as [LimitType, LimitType, LimitType, LimitType];
  }

  /**
   * Sets the limit at the given time
   * @param day Weekday
   * @param hour 0-23
   * @param quarter 0-3 (quarter of an hour)
   */
  public setLimit(day: Weekday, hour: number, quarter: number, limit: LimitType) {
    this.days[day][hour][quarter] = limit;
  }

  /**
   * Sets the limits in the given hour at the given day (each array index of `hourLimits` represents one quarter of the hour)
   * @param day Weekday
   * @param hour 0-23
   * @param hourLimits 0-3 (quarter of an hour)
   */
  public setHourLimits(day: Weekday, hour: number, hourLimits: [LimitType, LimitType, LimitType, LimitType]) {
    this.days[day][hour] = hourLimits;
  }

  /**
   * Decodes timetable string and stores the decoded data in the timetable object.
   * If decoding fails, no data is written to the object.
   * @returns False if the string is invalid and could not be decoded to a timetable
   */
  public fromEncodedTimetableString(encoded: string): boolean {
    if (encoded.length !== 7 * 24) {
      return false;
    }

    const daysTemp: LimitType[][][] = new Array(7).fill(0).map(() => new Array(24).fill(0).map(() => new Array(4).fill(LimitType.Allowed)));

    for (let day = 0; day < 7; day++) {
      // Day index as it is needed in the days array in this class (in this class: Monday = 0; encoded as string: Sunday = 0)
      const decodedDayIndex = (day + 6) % 7;

      for (let hour = 0; hour < 24; hour++) {
        const char = encoded.charAt(day * 24 + hour);
        const quarterLimits = this.encodedLimitMap[char];
        for (let quarter = 0; quarter < 4; quarter++) {
          if (quarterLimits[quarter] in LimitType) {
            daysTemp[decodedDayIndex][hour][quarter] = quarterLimits[quarter];
          } else {
            return false;
          }
        }
      }
    }

    // Write result if decoding was successful
    this.days = daysTemp;

    return true;
  }

  /**
   * Encodes the data of the timetable object as string
   */
  public toEncodedTimetableString(): string {
    let encoded = '';

    for (let day = 0; day < 7; day++) {
      // days array: Monday = 0; encoded as string: Sunday = 0)
      const decodedDayIndex = (day + 6) % 7;

      for (let hour = 0; hour < 24; hour++) {
        const quarterArr = this.days[decodedDayIndex][hour];
        const char = Object.keys(this.encodedLimitMap).find((key) => this.encodedLimitMap[key].every((val, i) => val === quarterArr[i]));
        encoded += char;
      }
    }

    return encoded;
  }
}

export enum LimitType {
  Allowed = 0,
  Blocked = 1
}
